// src/api/autoCycleApi.ts

import { buildApiUrl } from "./etfApi";

export interface AutoCycleEngineState {
  enabled: boolean;
  mode: "simulation" | "tracking";
  cycle_state: "IDLE" | "ACCUMULATING" | "HOLDING" | "EXITING" | "COOLDOWN";
  cycle_start_date?: string;
  avg_entry_price?: number;
  partial_exit_date?: string;
  cooldown_end_date?: string;
  account_value_usd?: number;
  max_position_pct?: number;
  real_entry_price?: number;
  real_shares?: number;
  legs_executed?: Array<{ leg_id?: string; shares?: number; price?: number }>;
}

export interface RotationState {
  rotation_mode: "soxl_only" | "soxs_only" | "auto_rotate";
  active_engine_key: "SOXL" | "SOXS";
  regime_state: "BULL" | "BEAR" | "NEUTRAL";
  bull_confirm_streak: number;
  bear_confirm_streak: number;
  switch_lockout_end_date?: string;
  switch_blocked: boolean;
  block_reason?: string;
  risk_profile?: "conservative" | "moderate" | "aggressive";
  crash_guard_enabled?: boolean;
  profile_summary?: {
    risk_profile: string;
    entry_strictness: string;
    entry_confirmation: string;
    accumulation_speed: string;
    exit_targets: string;
    crash_guard: string;
    rotation_confirm_days: number;
  };
  regime_data: {
    regime?: string;
    bull_regime_market?: boolean;
    bear_regime_market?: boolean;
    qqq_close?: number;
    qqq_sma200?: number;
    qqq_ema20?: number;
    qqq_ema50?: number;
    smh_close?: number;
    smh_ema50?: number;
    provider?: string;
  };
}

export interface EngineSummary {
  cycle_state: string;
  enabled: boolean;
  avg_entry_price?: number;
  real_shares?: number;
}

export interface PhaseInfo {
  phase: string;
  confidence: number;
  reasons: string[];
  phase_start_ts?: string;
  last_phase?: string;
  allowed_actions: {
    entries: boolean;
    adds: boolean;
    trims: boolean;
    exits: boolean;
    description: string;
  };
  description: string;
  /** Conditions that must be true to ENTER this phase */
  trigger_conditions?: string[];
  /** Conditions that can push FROM this phase back to another phase */
  reversion_conditions?: string[];
}

export interface AutoCycleStatusResponse {
  rotation_state: RotationState;
  active_engine_status: {
    engine_key: "SOXL" | "SOXS";
    engine_state: AutoCycleEngineState;
    recommended_action_now: string;
    phase?: PhaseInfo;
    recommended_action_detail: string;
    next_scheduled_action?: string;
    cycle_progress: {
      legs_complete: number;
      legs_total: number;
      legs: Array<{ id: string; executed: boolean }>;
    };
    key_levels: {
      sma200?: number;
      ema50?: number;
      ema20?: number;
      lower_bb?: number;
      atr14?: number;
      vwap?: number;
      rsi2?: number;
      rsi14?: number;
    };
    profit_pct_vs_entry?: number;
    risk_flags?: {
      buys_frozen: boolean;
      crash_guard_reason: string | null;
      atr_pct: number | null;
      drawdown_10d: number | null;
      drawdown_20d: number | null;
      next_buy_allowed: boolean;
    };
    data_meta: {
      provider: string;
      timestamp?: string;
      is_delayed: boolean;
      is_stale: boolean;
    };
  };
  soxl_engine_status?: EngineSummary;
  soxs_engine_status?: EngineSummary;
  badge_state: boolean;
}

export interface AutoCycleConfigRequest {
  enabled: boolean;
  rotation_mode?: "soxl_only" | "soxs_only" | "auto_rotate";
  mode?: "simulation" | "tracking";
  account_value_usd?: number;
  max_position_pct?: number;
  soxl_tracking_entry_price?: number;
  soxl_tracking_shares?: number;
  soxs_tracking_entry_price?: number;
  soxs_tracking_shares?: number;
  risk_profile?: "conservative" | "moderate" | "aggressive";
  crash_guard_enabled?: boolean;
}

export interface AutoCycleTradeLedgerEntry {
  id: number;
  user_id: string;
  engine_key: "SOXL" | "SOXS";
  ledger_type: "simulation" | "real";
  timestamp: string;
  action: "BUY" | "SELL" | "RISK_FREEZE" | "RISK_UNFREEZE";
  leg_id?: string;
  shares: number;
  price: number;
  notional: number;
  position_shares_after: number;
  avg_entry_price_after?: number;
  realized_pnl?: number;
  notes?: string;
  created_at: string;
  trade_style?: "AUTO_CYCLE" | "INTRADAY_EXEC";
  action_subtype?: string;
  phase?: string;
}

export interface AutoCycleLedgerResponse {
  ledger: AutoCycleTradeLedgerEntry[];
  count: number;
}

export interface BacktestResult {
  cycle_completed: boolean;
  total_cycles: number;
  win_rate: number;
  avg_cycle_return: number;
  max_drawdown: number;
  legs_executed: Array<{
    leg_id: string;
    execution_date: string;
    weekday: number;
    day_name: string;
    shares: number;
    price: number;
    notional: number;
  }>;
  exits: Array<{
    exit_id: string;
    execution_date: string;
    weekday: number;
    day_name: string;
    shares: number;
    price: number;
    realized_pnl: number;
  }>;
  summary: {
    total_invested: number;
    total_proceeds: number;
    total_pnl: number;
    total_return_pct: number;
  };
}

export async function getAutoCycleStatus(): Promise<AutoCycleStatusResponse> {
  const username = localStorage.getItem("username") || "";
  const response = await fetch(buildApiUrl("api/auto_cycle/status"), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-Username": username,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Unknown error" }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

export interface AutoCycleConfigResponse {
  status: string;
  message: string;
  rotation_state?: { enabled?: boolean; rotation_mode?: string; active_engine_key?: string; crash_guard_enabled?: boolean };
}

export async function updateAutoCycleConfig(
  config: AutoCycleConfigRequest
): Promise<AutoCycleConfigResponse> {
  const username = localStorage.getItem("username") || "";
  const response = await fetch(buildApiUrl("api/auto_cycle/config"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Username": username,
    },
    body: JSON.stringify(config),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Unknown error" }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

export async function acknowledgeAutoCycleAlert(): Promise<{ status: string; message: string }> {
  const username = localStorage.getItem("username") || "";
  const response = await fetch(buildApiUrl("api/auto_cycle/ack_alert"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Username": username,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Unknown error" }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

export async function getAutoCycleLedger(
  engine?: "SOXL" | "SOXS",
  type?: "simulation" | "real",
  limit: number = 200
): Promise<AutoCycleLedgerResponse> {
  const username = localStorage.getItem("username") || "";
  const params = new URLSearchParams();
  if (engine) params.append("engine", engine);
  if (type) params.append("type", type);
  params.append("limit", limit.toString());

  const response = await fetch(buildApiUrl(`api/auto_cycle/ledger?${params.toString()}`), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-Username": username,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Unknown error" }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

export async function deleteAutoCycleLedgerEntries(
  entryIds: number[]
): Promise<{ deleted_count: number; message: string }> {
  const username = localStorage.getItem("username") || "";
  const response = await fetch(buildApiUrl("api/auto_cycle/ledger/delete"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Username": username,
    },
    body: JSON.stringify({
      entry_ids: entryIds,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Unknown error" }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

export async function recordAutoCycleRealTrade(
  engine_key: "SOXL" | "SOXS",
  action: "BUY" | "SELL",
  shares: number,
  price: number,
  leg_id?: string,
  notes?: string
): Promise<{ status: string; message: string; entry_id: number }> {
  const username = localStorage.getItem("username") || "";
  const response = await fetch(buildApiUrl("api/auto_cycle/record_real_trade"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Username": username,
    },
    body: JSON.stringify({
      engine_key,
      action,
      shares,
      price,
      leg_id,
      notes,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Unknown error" }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

export async function forceCloseAutoCycle(
  engine_key: "SOXL" | "SOXS",
  exit_price: number,
  notes?: string
): Promise<{ status: string; message: string; shares_closed: number; exit_price: number; realized_pnl?: number; recommendation_id: string }> {
  const username = localStorage.getItem("username") || "";
  const response = await fetch(buildApiUrl("api/auto_cycle/force_close"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Username": username,
    },
    body: JSON.stringify({
      engine_key,
      exit_price,
      notes,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Unknown error" }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

export async function closeAutoCycleLeg(
  engine_key: "SOXL" | "SOXS",
  leg_id: string,
  exit_price: number,
  notes?: string,
  shares?: number
): Promise<{ status: string; message: string; leg_id: string; shares_closed: number; exit_price: number; realized_pnl?: number; recommendation_id: string; remaining_shares: number }> {
  const username = localStorage.getItem("username") || "";
  const body: Record<string, unknown> = { engine_key, leg_id, exit_price };
  if (notes != null) body.notes = notes;
  if (shares != null) body.shares = shares;
  const response = await fetch(buildApiUrl("api/auto_cycle/close_leg"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Username": username,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Unknown error" }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

export async function runAutoCycleBacktest(
  engine_key: "SOXL" | "SOXS",
  start_date: string,
  account_value_usd: number = 10000.0,
  max_position_pct: number = 50.0,
  end_date?: string,
  risk_profile?: "conservative" | "moderate" | "aggressive",
  intraday_overlay_enabled: boolean = false
): Promise<BacktestResult & {
  intraday_overlay_enabled?: boolean;
  iel_actions?: Array<{ date: string; action: string; price?: number; shares?: number; trigger?: string }>;
  iel_legs_executed?: any[];
  iel_trims_executed?: any[];
  comparison_metrics?: {
    auto_cycle_only: { total_pnl: number; total_return_pct: number; legs_count: number; exits_count: number };
    with_iel_overlay: { total_pnl: number; total_return_pct: number; legs_count: number; exits_count: number; iel_legs_count: number; iel_trims_count: number };
    incremental: { pnl_improvement: number; return_pct_improvement: number; improvement_pct: number };
  };
}> {
  const username = localStorage.getItem("username") || "";
  const response = await fetch(buildApiUrl("api/auto_cycle/backtest"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Username": username,
    },
    body: JSON.stringify({
      engine_key,
      start_date,
      account_value_usd,
      max_position_pct,
      end_date,
      risk_profile,
      intraday_overlay_enabled,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Unknown error" }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

// ===== INTRADAY EXECUTION LAYER API =====

export interface IntradayStatusResponse {
  enabled: boolean;
  profile: "conservative" | "moderate" | "aggressive";
  timeframe: "5m" | "15m";
  engine_key: "SOXL" | "SOXS";
  current_recommendation: {
    action: "BUY_ADD" | "HOLD" | "PAUSE" | "TRIM" | "EXIT_CONFIRMED";
    reason: string;
    levels: {
      vwap?: number;
      ema20?: number;
      ema50?: number;
      rsi5?: number;
      orb_high?: number;
      orb_low?: number;
      session_high?: number;
      session_low?: number;
      bb_upper?: number;
      bb_lower?: number;
      current_price?: number;
    };
    trigger_indicators: string[];
    metadata: Record<string, any>;
  };
  last_signal: {
    timestamp?: string;
    action?: string;
    reason?: string;
  };
  paused_until?: string;
}

export interface IntradayConfigRequest {
  enabled: boolean;
  profile?: "conservative" | "moderate" | "aggressive";
  timeframe?: "5m" | "15m";
}

export async function getIntradayStatus(): Promise<IntradayStatusResponse> {
  const username = localStorage.getItem("username") || "";
  const response = await fetch(buildApiUrl("api/auto_cycle/intraday/status"), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-Username": username,
    },
  });

  if (!response.ok) {
    // Handle 404 gracefully - IEL might not be available yet
    if (response.status === 404) {
      throw new Error("IEL endpoint not found - server may need restart");
    }
    const error = await response.json().catch(() => ({ detail: "Unknown error" }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

export async function updateIntradayConfig(
  config: IntradayConfigRequest
): Promise<{ status: string; message: string }> {
  const username = localStorage.getItem("username") || "";
  const response = await fetch(buildApiUrl("api/auto_cycle/intraday/config"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Username": username,
    },
    body: JSON.stringify(config),
  });

  if (!response.ok) {
    // Handle 404 gracefully
    if (response.status === 404) {
      throw new Error("IEL endpoint not found - server may need restart");
    }
    const error = await response.json().catch(() => ({ detail: "Unknown error" }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

export async function reconfirmIntraday(): Promise<{
  status: string;
  still_valid: boolean;
  updated_recommendation: IntradayStatusResponse["current_recommendation"];
}> {
  const username = localStorage.getItem("username") || "";
  const response = await fetch(buildApiUrl("api/auto_cycle/intraday/reconfirm"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Username": username,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Unknown error" }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

export async function getIntradayLedger(
  engine?: "SOXL" | "SOXS",
  limit: number = 200
): Promise<AutoCycleLedgerResponse> {
  const username = localStorage.getItem("username") || "";
  const params = new URLSearchParams();
  if (engine) params.append("engine", engine);
  params.append("limit", limit.toString());

  const response = await fetch(buildApiUrl(`api/auto_cycle/intraday/ledger?${params.toString()}`), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-Username": username,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Unknown error" }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

